import { Form, Button, Alert, InputGroup } from 'react-bootstrap';
import React, { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import axiosInstance from '../general/axiosInstance';
import { setIsLoading } from '../general/actions';
import AlertModal from '../general/alertModal';
import OrderCreate from './manage';
import Log from './log';
import { useParams, useNavigate } from 'react-router-dom';
import { currencyFormater } from '../general/utils';

const STATUS_STYLE = {
    1: { bg: '#fef9c3', color: '#854d0e', border: '#fde047' },
    2: { bg: '#dbeafe', color: '#1e40af', border: '#93c5fd' },
    3: { bg: '#dcfce7', color: '#166534', border: '#86efac' },
};

function RecentOrderCard({ order, is_provider, onClick }) {
    const s = STATUS_STYLE[order.order_status?.id] || { bg: '#f1f5f9', color: '#475569', border: '#cbd5e1' };
    const counterpart = is_provider
        ? order.agreement?.customer?.name
        : order.agreement?.provider?.name;

    return (
        <div className="recent-order-card" onClick={onClick}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
                <span style={{ fontSize: 18, fontWeight: 800, color: 'var(--abi-primary)' }}>#{order.id}</span>
                <span className="report-status-badge" style={{ background: s.bg, color: s.color, border: `1px solid ${s.border}` }}>
                    {order.order_status?.description}
                </span>
            </div>
            {counterpart && (
                <div style={{ fontSize: 12, color: 'var(--abi-muted)', fontWeight: 500, marginBottom: 4, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {counterpart}
                </div>
            )}
            <div style={{ fontSize: 11, color: 'var(--abi-muted)', marginBottom: 2 }}>
                <i className="fa fa-map-marker" style={{ marginRight: 4 }} />
                {order.delivery_date} {order.delivery_time}
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8, paddingTop: 8, borderTop: '1px solid var(--abi-border)' }}>
                <span style={{ fontSize: 11, color: 'var(--abi-muted)' }}>
                    <i className="fa fa-cube" style={{ marginRight: 3 }} />
                    {order.order_detail_count ?? '—'} producto{order.order_detail_count !== 1 ? 's' : ''}
                </span>
                <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--abi-text)' }}>
                    {currencyFormater(order.total_amount)}
                </span>
            </div>
        </div>
    );
}

function Edit(props) {
    const navigate = useNavigate();
    const { setIsLoading, roles, readonly, is_provider } = props;
    const { order } = useParams();

    const [orderId, setOrderId] = useState('');
    const [orderIdToSearch, setOrderIdToSearch] = useState('');
    const [orderData, setOrderData] = useState([]);

    const [alertModalShow, setAlertModalShow] = useState(false);
    const [alertModalMsg, setAlertModalMsg] = useState('');
    const [alertModalOnConfirm, setAlertModalOnConfirm] = useState(null);
    const [alertModalOnClose, setAlertModalOnClose] = useState(null);

    const [message, setMessage] = useState('');
    const [messageType, setMessageType] = useState('warning');

    const [recentOrders, setRecentOrders] = useState([]);
    const [loadingRecent, setLoadingRecent] = useState(true);
    const [logShow, setLogShow] = useState(false);
    const orderDetailRef = useRef(null);

    useEffect(() => { if (order) searchOrder(order); }, [order]);

    useEffect(() => {
        if (orderId && orderDetailRef.current) {
            orderDetailRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    }, [orderId]);

    useEffect(() => {
        const controller = new AbortController();
        const fetchRecent = async () => {
            try {
                const today = new Date();
                const past  = new Date();
                past.setDate(today.getDate() - 60);
                const fmt = d => d.toISOString().slice(0, 10);
                const r = await axiosInstance.get('/orders/recent', {
                    params: { my_orders: true, created_start_date: fmt(past), created_end_date: fmt(today) },
                    signal: controller.signal,
                });
                setRecentOrders(r.data || []);
            } catch (e) {
                if (e.code !== 'ERR_CANCELED') console.error(e);
            } finally { setLoadingRecent(false); }
        };
        fetchRecent();
        return () => controller.abort();
    }, []);

    const setOrderDataToBeUpdated = (data) => {
        const orderDetail = data.order_detail.map(detail => ({
            id_detail: detail.id,
            quantity: detail.quantity,
            id: detail.product.id,
            ext_id: detail.product.ext_id,
            name: detail.product.name,
            taxes: parseFloat(detail.taxes) || 0,
            img_url: detail.product.img_url,
            description: detail.product.description,
            price: parseFloat(detail.price) || 0,
            product_type: { name: detail.product.product_type.name },
        }));

        data.order_detail = orderDetail;

        const arrDate = data.delivery_date.split('/');
        data.delivery_date = new Date(`${arrDate[2]}-${arrDate[1]}-${arrDate[0]}`).toISOString().slice(0, 10);

        setOrderData(data);

        if (data.order_status.id > 2 && !roles.includes('provider_order') && !readonly) {
            setAlertModalShow(true);
            setAlertModalMsg('No se puede modificar un pedido aprobado por el proveedor');
            setAlertModalOnClose(() => () => { navigate('/order/search/' + data.id); });
        }
    };

    const searchOrder = async (id) => {
        setIsLoading(true);
        try {
            const response = await axiosInstance.get(`/orders/${id}`);
            const idFound = response.data.id;
            if (idFound) {
                setMessage('');
                setOrderId(idFound);
                setOrderDataToBeUpdated(response.data);
            } else {
                setOrderId('');
                setMessageType('info');
                setMessage('Pedido no encontrado');
            }
        } catch (error) {
            setMessageType('danger');
            if (error.response.data.message.indexOf('Duplicate') > 0) {
                setMessage('Tipo de produto ya existe');
            } else {
                setMessage(error.message);
            }
        } finally {
            setOrderIdToSearch('');
            setIsLoading(false);
        }
    };

    return (
        <>
            {/* ── Ayuda ── */}
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, background: 'rgba(37,99,235,0.05)', border: '1px solid rgba(37,99,235,0.15)', borderRadius: 12, padding: '12px 16px', marginBottom: 16 }}>
                <i className="fa fa-lightbulb-o" style={{ color: 'var(--abi-primary)', fontSize: 18, marginTop: 2, flexShrink: 0 }} />
                <div style={{ fontSize: 13, color: 'var(--abi-text)', lineHeight: 1.65 }}>
                    <strong>¿Cómo consultar un pedido?</strong>
                    <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
                        <li>Escribe el <strong>número de pedido</strong> en la barra de búsqueda y presiona <strong>Buscar</strong> o <kbd style={{ fontSize: 11, background: '#094c8f', border: '1px solid var(--abi-border)', borderRadius: 4, padding: '0 4px' }}>Enter</kbd>.</li>
                        <li>O haz clic en cualquier pedido de la lista de <strong>recientes</strong> para cargarlo directamente.</li>
                        <li>Desde el detalle puedes ver el <strong>historial de cambios</strong> e <strong>imprimir</strong> el pedido.</li>
                    </ul>
                </div>
            </div>

            {/* ── Pedidos recientes ── */}
            {!loadingRecent && recentOrders.length > 0 && (
                <div style={{ marginBottom: 20 }}>
                    <div className="report-field-label" style={{ marginBottom: 10 }}>
                        <i className="fa fa-clock-o" style={{ marginRight: 6 }} />
                        Últimos pedidos — 60 días
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))', gap: 10 }}>
                        {recentOrders.map(o => (
                            <RecentOrderCard
                                key={o.id}
                                order={o}
                                is_provider={is_provider}
                                onClick={() => navigate((readonly ? '/order/search/' : '/order/edit/') + o.id)}
                            />
                        ))}
                    </div>
                </div>
            )}

            {/* ── Buscador ── */}
            <div className="order-search-bar">
                <div className="order-search-bar-inner">
                    <div className="order-search-bar-title">
                        <i className="fa fa-search" /> Buscar pedido
                    </div>
                    <InputGroup style={{ maxWidth: 340 }}>
                        <InputGroup.Text style={{ background: 'var(--abi-bg)', border: '1px solid var(--abi-border)', borderRight: 'none' }}>
                            <i className="fa fa-hashtag" style={{ color: 'var(--abi-muted)' }} />
                        </InputGroup.Text>
                        <Form.Control
                            type="number"
                            value={orderIdToSearch}
                            onChange={(e) => { e.preventDefault(); setOrderIdToSearch(e.target.value); }}
                            onKeyPress={(e) => { e.key === 'Enter' && searchOrder(e.target.value); }}
                            placeholder="Número de pedido"
                            style={{ borderLeft: 'none' }}
                        />
                        <Button
                            variant="primary"
                            disabled={!orderIdToSearch}
                            onClick={() => searchOrder(orderIdToSearch)}
                        >
                            Buscar
                        </Button>
                    </InputGroup>
                </div>
                <hr className="mb-0" />
            </div>

            {message && (
                <Alert variant={messageType || 'warning'} className="mt-3">{message}</Alert>
            )}

            {orderId && (
                <div className="mt-3" ref={orderDetailRef}>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginBottom: 10 }}>
                        {readonly &&
                            (roles.includes('customer_order') || roles.includes('provider_order') || roles.includes('customer_admin')) &&
                            (orderData.order_status?.id <= 2 || roles.includes('provider_order')) && (
                            <Button
                                variant="outline-primary"
                                size="sm"
                                onClick={() => navigate('/order/edit/' + orderId)}
                                style={{ display: 'flex', alignItems: 'center', gap: 6 }}
                            >
                                <i className="fa fa-pencil" />
                                Editar pedido
                            </Button>
                        )}
                        <Button
                            variant="outline-secondary"
                            size="sm"
                            onClick={() => setLogShow(true)}
                            style={{ display: 'flex', alignItems: 'center', gap: 6 }}
                        >
                            <i className="fa fa-history" />
                            Historial de cambios
                        </Button>
                    </div>
                    <OrderCreate
                        orderId={orderId}
                        setOrderId={setOrderId}
                        orderData={orderData}
                        readonly={readonly}
                    />
                </div>
            )}

            <Log
                show={logShow}
                setShow={setLogShow}
                orderId={orderId}
            />

            <AlertModal
                show={alertModalShow} setShow={setAlertModalShow}
                msg={alertModalMsg} setMsg={setAlertModalMsg}
                onConfirm={alertModalOnConfirm} setOnConfirm={setAlertModalOnConfirm}
                onClose={alertModalOnClose} setOnClose={setAlertModalOnClose}
            />
        </>
    );
}

function mapStateToProps(state) {
    return {
        isLoading: state.isLoading,
        roles: state.roles,
        is_provider: state.is_provider,
    };
}

const mapDispatchToProps = { setIsLoading };

export default connect(mapStateToProps, mapDispatchToProps)(Edit);
